import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {CommonModule} from "@angular/common";
import {FormsModule} from "@angular/forms";
import {SwitchBoard} from "../../../services/SwitchBoard";
import {ToastService} from "../../../services/Toast.service";
import {LoadingService} from "../../../services/LoadingService";
import {TOAST_TYPE} from "../../../constants/Tpiq.constants";
import {TaxPlanningService} from "../../../tpiq-services/tax-planning.service";
import {takeUntil} from "rxjs/operators";
import {Session} from "../../../services/Session";
import {v4 as uuid} from 'uuid';
import {FormatAmountPipe} from "../../../pipes/format-amount.pipe";
import { evaluate } from 'mathjs';

declare const _: any;

@Component({
  selector: 'app-formula-builder',
  standalone: true,
  imports: [CommonModule, FormsModule, FormatAmountPipe],
  templateUrl: './formula-builder.component.html',
  styleUrl: './formula-builder.component.scss'
})
export class FormulaBuilderComponent implements OnInit, OnDestroy {
  formulaValid;
  sampleCalculatedTaxValue;
  formulaRatesNamesArray: Array<any> = [];
  formulaRatesIdsArray: Array<any> = [];
  allAssociatedFormulaRatesData: any = {};
  tempCustomRateObject: any = {
    id : '',
    name : '',
    rate : 0,
    formula: ''
  };
  finalFormula = {
    displayValue: '',
    actualValue: '',
    displayValuesArray: [],
    actualValuesArray: [],
    type: ''
  };
  user: any;
  org: string;

  @Input() actionType: string;
  @Input() clientTaxPlan: any;
  @Input() associatedWith: any;
  @Input() allAssociatedData: any;
  @Input() fixedRatesArray: any;
  @Input() customRate: any;
  @Input() addingCustomRatesArray: any;
  @Input() isCustomRateEdit: boolean;
  @Input() indexOfTempCustomObj: any;

  @Output() onCustomRateAction: EventEmitter<any> = new EventEmitter<any>();
  @Output() onProgpageCustomRate: EventEmitter<any> = new EventEmitter<any>();
  @Output() triggerRecalcAndUpdatePlan: EventEmitter<any> = new EventEmitter<any>();
  @Output() onHideReveal: EventEmitter<void> = new EventEmitter<void>();
  @Output() refreshTableData: EventEmitter<void> = new EventEmitter<void>();
  @Output() onUpdateTaxRatesInFormula: EventEmitter<void> = new EventEmitter<void>();
  @Output() buildAssociatedObject: EventEmitter<void> = new EventEmitter<void>();
  @Output() destroy$: EventEmitter<void> = new EventEmitter<void>();
  @Output() triggerCloseEditCustomRate: EventEmitter<any> = new EventEmitter<any>();
  @Output() triggerCloseAddCustomRateInStrategyPage: EventEmitter<any> = new EventEmitter<any>();
  @Output() onCustomRateActionOnEstimate: EventEmitter<any> = new EventEmitter<any>();

  constructor(
    private loadingService: LoadingService,
    private toastService: ToastService,
    private taxPlanningService: TaxPlanningService,
    private switchboard: SwitchBoard
  ) {}

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  ngOnInit(): void {
    this.user = Session.getUser();
    this.org = Session.getOrg();
    this.switchboard.onRecalculateCustomRates.pipe(takeUntil(this.destroy$)).subscribe(key => {
      this.reCalculateCustomRates(key);
    });

    this.switchboard.onUpdateCustomRate.pipe(takeUntil(this.destroy$)).subscribe(key => {
      this.updateCustomTaxRate(key);
    });

    if (this.customRate) {
      const obj: any = {};
      obj.id = this.customRate.id;
      obj.name = this.customRate.name;
      obj.rate = this.customRate.rate;
      obj.formula = this.customRate.formula || {
        displayValue: '',
        actualValue: '',
        displayValuesArray: [],
        actualValuesArray: [],
        type: ''
      };
      this.tempCustomRateObject = obj;
      this.finalFormula = this.tempCustomRateObject.formula;
      this.formulaRatesNamesArray = this.finalFormula.displayValuesArray || [];
      this.formulaRatesIdsArray = this.finalFormula.actualValuesArray || [];
      this.isValidFormula(this.finalFormula.actualValue);
      if (this.customRate.id) {
        this.isCustomRateEdit = true;
      }
    }
  }


  hideReveal(){
    this.onHideReveal.emit();
  }

  updateFormula(value, type?) {
    if (value === 'clear') {
      this.finalFormula = {
        displayValue: '',
        actualValue: '',
        displayValuesArray: [],
        actualValuesArray: [],
        type: this.finalFormula.type
      };
      this.formulaRatesNamesArray = [];
      this.formulaRatesIdsArray = [];
    } else if (value === 'back') {
      if (this.formulaRatesNamesArray.length > 0) {
        this.formulaRatesNamesArray.pop();
      }
      if (this.formulaRatesIdsArray.length > 0) {
        this.formulaRatesIdsArray.pop();
      }
      this.finalFormula = {
        displayValue: this.getFormulaDisplayString(this.formulaRatesNamesArray),
        actualValue: this.getFormulaDisplayString(this.formulaRatesIdsArray),
        displayValuesArray: this.formulaRatesNamesArray,
        actualValuesArray: this.formulaRatesIdsArray,
        type: this.finalFormula.type
      };
    } else {
      if (type && type === 'rates') {
        this.formulaRatesNamesArray.push(value.name);
        this.formulaRatesIdsArray.push(value.id);
      } else {
        this.formulaRatesNamesArray.push(value);
        this.formulaRatesIdsArray.push(value);
      }
      this.finalFormula = {
        displayValue: this.getFormulaDisplayString(this.formulaRatesNamesArray),
        actualValue: this.getFormulaDisplayString(this.formulaRatesIdsArray),
        displayValuesArray: this.formulaRatesNamesArray,
        actualValuesArray: this.formulaRatesIdsArray,
        type: this.finalFormula.type
      };
    }
    this.isValidFormula(this.finalFormula.actualValue);
  }

  getFormulaDisplayString(data) {
    const base = this;
    let displayString = '';
    if (data && data.length > 0) {
      _.each(data, function(field){
        displayString += field;
      });
    }
    return displayString;
  }

  isValidFormula(value) {
    const base = this;
    const formula = this.getRateFormula(value);
    try {
      const result = evaluate(formula);
      if (typeof result === 'number' && result) {
        base.formulaValid = true;
        base.sampleCalculatedTaxValue = (result / 100) * 1000;
      } else {
        base.formulaValid = false;
      }
    } catch (e) {
      base.formulaValid = false;
    }
    return base.formulaValid;
  }

  createOrUpdateCustomRate() {
    if (!this.tempCustomRateObject.name) {
      this.toastService.pop(TOAST_TYPE.error, 'Please enter custom tax rate name');
    } else if (!this.finalFormula.actualValue) {
      this.toastService.pop(TOAST_TYPE.error, 'Please enter custom tax rate');
    } else if (this.isValidFormula(this.finalFormula.actualValue)) {
      const customRate: any = {};
      customRate.id = this.tempCustomRateObject.id;
      customRate.name = this.tempCustomRateObject.name;
      customRate.rate = evaluate(this.getRateFormula(this.finalFormula.actualValue));
      customRate.formula = JSON.stringify(this.finalFormula);

      if (this.actionType == 'create'){
        if (!this.isCustomRateEdit) {
          this.createCustomRate(customRate);
        } else {
          this.updateCustomTaxRate(customRate, true);
        }
      } else if (this.actionType == 'emit'){
        if (!this.isCustomRateEdit) {
          customRate.id = uuid();
        }
        this.onCustomRateAction.next({
          action: this.isCustomRateEdit ? 'update' : 'add',
          customRate
        });
        this.hideReveal();
      }
    } else {
      this.toastService.pop(TOAST_TYPE.error, 'Formula is invalid');
    }
  }

  getRateFormula(value) {
    const base = this;
    let formula = _.cloneDeep(value) || 0;
    _.each(this.fixedRatesArray, function(obj){
      formula = _.replace(formula, obj.id, obj.rate);
    });
    return formula;
  }

  updateCustomTaxRate(customRate, updatePlan?){
    const base = this;
    this.loadingService.triggerLoadingEvent(true);
    const oldRate = _.find(this.clientTaxPlan.customTaxRates, {id: customRate.id});
    const oldCustomRate = _.cloneDeep(oldRate);
    this.taxPlanningService.updateCustomRate(Session.getOrg(), this.user.id, this.clientTaxPlan.id, customRate).subscribe(response => {
      this.loadingService.triggerLoadingEvent(false);
      this.onProgpageCustomRate.emit(customRate);
      if (updatePlan){
        this.triggerRecalcAndUpdatePlan.emit();
        this.triggerCloseEditCustomRate.emit(customRate);
      }
      setTimeout(() => {
        this.hideReveal();
      });
    }, error => {
      this.loadingService.triggerLoadingEvent(false);
      setTimeout(() => {
        this.hideReveal();
      });
      const message = error.message ? error.message : 'Failed to update Custom Rate';
      try {
        this.toastService.pop(TOAST_TYPE.error, message);
      } catch (e) {
        this.toastService.pop(TOAST_TYPE.error, message);
      }
    });
  }

  createCustomRate(customRate){
    if (customRate.name && customRate.rate){
      this.loadingService.triggerLoadingEvent(true);
      customRate.id = uuid();
      this.taxPlanningService.addEditCustomRate(Session.getOrg(), this.user.id, this.clientTaxPlan.id, customRate).subscribe(response => {
        this.loadingService.triggerLoadingEvent(false);
        this.toastService.pop(TOAST_TYPE.success, 'Custom rate created successfully');
        customRate.formula = JSON.parse(customRate.formula);
        this.clientTaxPlan.customTaxRates.push(customRate);
        if (this.indexOfTempCustomObj !== undefined && this.indexOfTempCustomObj !== null && this.indexOfTempCustomObj !== -1) {
          this.addingCustomRatesArray.splice(this.indexOfTempCustomObj, 1);
        }
        this.hideReveal();
        this.associatedWith.push(customRate);
        _.each(this.allAssociatedData, function(associatedFields, strategyId){
          associatedFields.push(customRate);
        });
        this.onCustomRateActionOnEstimate.emit(customRate);
        this.buildAssociatedObject.emit();
        this.triggerCloseAddCustomRateInStrategyPage.emit(customRate);
        this.refreshTableData.emit();
      }, error => {
        this.loadingService.triggerLoadingEvent(false);
        const message = error.message ? error.message : 'Failed to create Custom Rate';
        try {
          this.toastService.pop(TOAST_TYPE.error, message);
        } catch (e) {
          this.toastService.pop(TOAST_TYPE.error, message);
        }
      });
    } else if (!customRate.name){
      this.toastService.pop(TOAST_TYPE.error, 'Please enter custom tax rate name');
    } else if (!customRate.rate){
      this.toastService.pop(TOAST_TYPE.error, 'Please enter custom tax rate');
    }
  }

  reCalculateCustomRates(keyName)  {
    const base = this;
    this.onUpdateTaxRatesInFormula.next();
    _.each(base.clientTaxPlan.customTaxRates, function(customRate){
      const updatedCustomRate = {...customRate};
      if (updatedCustomRate.formula) {
        const formula = updatedCustomRate.formula;
        updatedCustomRate.rate = evaluate(base.getRateFormula(formula.actualValue));
        const index = _.findIndex(base.clientTaxPlan.customTaxRates, {id: customRate.id});
        base.clientTaxPlan.customTaxRates[index] = _.cloneDeep(updatedCustomRate);
        updatedCustomRate.formula = JSON.stringify(updatedCustomRate.formula);
        base.updateCustomTaxRate(updatedCustomRate);
      }
    });
    this.triggerRecalcAndUpdatePlan.emit();
  }

}
